import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { formatHistoryTimestamp } from '@/utils/date'
import type { EntityHistoryRow } from './EntityHistoryCard'
import type { HistoryActions } from './history-detail'
import { historyActorText } from './identity-row-text'

const DETAIL = 'investigations.entityDetail.history.detail'

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="min-w-0 space-y-0.5">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="break-words whitespace-pre-wrap text-foreground">{children}</dd>
    </div>
  )
}

function PropertyDetail({ row }: { row: EntityHistoryRow }) {
  const { t } = useTranslation()
  const property = row.property
  if (!property) return null
  const none = t('investigations.entityDetail.history.header.noValue')
  return (
    <>
      <Field label={t(`${DETAIL}.property`)}>{property.key}</Field>
      <Field label={t(`${DETAIL}.${property.pinned ? 'retainedValue' : 'previousValue'}`)}>
        {property.old ?? none}
      </Field>
      <Field label={t(`${DETAIL}.${property.pinned ? 'unacceptedValue' : 'newValue'}`)}>
        {property.new ?? none}
      </Field>
      {property.previousDocumentName && (
        <Field label={t(`${DETAIL}.previousSource`)}>{property.previousDocumentName}</Field>
      )}
    </>
  )
}

function EvidenceDetail({ row }: { row: EntityHistoryRow }) {
  const { t } = useTranslation()
  const detail = row.detail
  return (
    <>
      {detail?.otherEntity && (
        <Field label={t(`${DETAIL}.otherEntity`)}>
          {detail.otherEntity.name ?? t('investigations.entityDetail.history.identity.unnamed')}
          {detail.otherEntity.uid && (
            <span className="mt-1 block break-all text-2xs text-muted-foreground">
              {t(`${DETAIL}.entityId`)}: {detail.otherEntity.uid}
            </span>
          )}
        </Field>
      )}
      {detail?.relationship && (
        <Field label={t(`${DETAIL}.relationship`)}>{detail.relationship}</Field>
      )}
      {detail?.mentionedAs && (
        <Field label={t(`${DETAIL}.mentionedAs`)}>{detail.mentionedAs}</Field>
      )}
      {row.identity?.reason && (
        <Field label={t(`${DETAIL}.reason`)}>
          {t(`investigations.entityDetail.history.identity.reasons.${row.identity.reason}`)}
        </Field>
      )}
      {row.fact?.note && <Field label={t(`${DETAIL}.note`)}>{row.fact.note}</Field>}
      {(row.documentId || row.documentName) && (
        <Field label={t(`${DETAIL}.source`)}>
          {row.documentName ?? t('investigations.entityDetail.unknownDocument')}
        </Field>
      )}
      {detail?.sourceText && <Field label={t(`${DETAIL}.sourceText`)}>{detail.sourceText}</Field>}
    </>
  )
}

function EventMetadata({ row }: { row: EntityHistoryRow }) {
  const { t, i18n } = useTranslation()
  const actor = row.detail?.actor
  return (
    <>
      <Field label={t(`${DETAIL}.recordedAt`)}>
        <time dateTime={row.learnedAt}>{formatHistoryTimestamp(row.learnedAt, i18n.language)}</time>
      </Field>
      {row.detail?.occurredAt && (
        <Field label={t(`${DETAIL}.occurredAt`)}>
          <time dateTime={row.detail.occurredAt}>
            {formatHistoryTimestamp(row.detail.occurredAt, i18n.language)}
          </time>
        </Field>
      )}
      {actor && <Field label={t(`${DETAIL}.actor`)}>{historyActorText(actor, t)}</Field>}
    </>
  )
}

function EventActions({ row, actions }: { row: EntityHistoryRow; actions: HistoryActions }) {
  const { t } = useTranslation()
  const documentId = row.documentId
  const auditId = row.detail?.auditId
  return (
    <div className="flex flex-wrap gap-2">
      {documentId && actions.onOpenDocument && (
        <Button variant="outline" size="sm" onClick={() => actions.onOpenDocument?.(documentId)}>
          {t(`${DETAIL}.openSource`)}
        </Button>
      )}
      {auditId && actions.onOpenAudit && (
        <Button variant="outline" size="sm" onClick={() => actions.onOpenAudit?.(auditId)}>
          {t(`${DETAIL}.openAudit`)}
        </Button>
      )}
    </div>
  )
}

export function HistoryEventDetail({
  row,
  actions,
  description,
}: {
  row: EntityHistoryRow
  actions: HistoryActions
  description?: string
}) {
  return (
    <div className="space-y-3 border-t border-border/50 px-3 py-3 text-xs">
      {description && <p className="break-words leading-relaxed">{description}</p>}
      <dl className="space-y-3">
        <EventMetadata row={row} />
        <PropertyDetail row={row} />
        <EvidenceDetail row={row} />
      </dl>
      <EventActions row={row} actions={actions} />
    </div>
  )
}
